/**
 * 公共工具函数库
 */

/**
 * 格式化日期时间
 * @param {string|Date} date 日期字符串或Date对象
 * @param {string} format 格式类型：'datetime' | 'date' | 'time'
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(date, format = 'datetime') {
  if (!date) return '-'
  const d = new Date(date)
  if (isNaN(d.getTime())) return date
  
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  
  switch (format) {
    case 'date':
      return `${year}-${month}-${day}`
    case 'time':
      return `${hours}:${minutes}:${seconds}`
    case 'datetime':
    default:
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  }
}

/**
 * 格式化价格（分转元）
 * @param {number} priceInCents 价格（分）
 * @param {number} precision 小数位数，默认2位
 * @returns {string} 格式化后的价格字符串
 */
export function formatPrice(priceInCents, precision = 2) {
  if (priceInCents == null || priceInCents === '') return '0.00'
  const price = Number(priceInCents)
  if (isNaN(price)) return '0.00'
  return (price / 100).toFixed(precision)
}

/**
 * 格式化相对时间
 * @param {string|Date} date 日期字符串或Date对象
 * @returns {string} 相对时间字符串
 */
export function formatRelativeTime(date) {
  if (!date) return '-'
  const now = new Date()
  const target = new Date(date)
  if (isNaN(target.getTime())) return '-'
  
  const diff = now - target
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)
  
  if (years > 0) return `${years}年前`
  if (months > 0) return `${months}个月前`
  if (days > 0) return `${days}天前`
  if (hours > 0) return `${hours}小时前`
  if (minutes > 0) return `${minutes}分钟前`
  if (seconds > 0) return `${seconds}秒前`
  return '刚刚'
}

/**
 * 从URL提取文件名
 * @param {string} url 文件URL
 * @returns {string} 文件名
 */
export function getFileName(url) {
  if (!url) return ''
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    const fileName = pathname.split('/').pop() || ''
    // 解码URL编码的文件名
    return decodeURIComponent(fileName)
  } catch (e) {
    // 如果不是完整URL，直接取最后一部分
    const parts = url.split('/')
    const fileName = parts[parts.length - 1] || ''
    try {
      return decodeURIComponent(fileName)
    } catch (e2) {
      return fileName
    }
  }
}

/**
 * 截取文件名
 * @param {string} fileName 完整文件名
 * @param {number} maxLength 最大长度
 * @returns {string} 截取后的文件名
 */
export function truncateFileName(fileName, maxLength = 30) {
  if (!fileName) return ''
  if (fileName.length <= maxLength) return fileName
  
  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex === -1) {
    // 没有扩展名
    return fileName.substring(0, maxLength - 3) + '...'
  }
  
  const ext = fileName.substring(lastDotIndex)
  const name = fileName.substring(0, lastDotIndex)
  const truncatedName = name.substring(0, maxLength - ext.length - 3) + '...'
  return truncatedName + ext
}

/**
 * 获取文件类型
 * @param {string} url 文件URL或文件名
 * @returns {string} 文件类型描述
 */
export function getFileType(url) {
  if (!url) return '未知'
  const fileName = getFileName(url)
  const ext = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase()
  
  const typeMap = {
    // 视频
    'mp4': '视频',
    'avi': '视频',
    'mov': '视频',
    'wmv': '视频',
    'flv': '视频',
    'mkv': '视频',
    'webm': '视频',
    'm4v': '视频',
    // 压缩包
    'zip': '压缩包',
    'rar': '压缩包',
    '7z': '压缩包',
    'tar': '压缩包',
    'gz': '压缩包',
    'bz2': '压缩包',
    // 电子书
    'epub': '电子书',
    'pdf': 'PDF',
    'mobi': '电子书',
    'azw': '电子书',
    // Office文档
    'doc': 'Word',
    'docx': 'Word',
    'xls': 'Excel',
    'xlsx': 'Excel',
    'ppt': 'PPT',
    'pptx': 'PPT',
    // 文本
    'txt': '文本',
    'md': 'Markdown',
    // 图片
    'jpg': '图片',
    'jpeg': '图片',
    'png': '图片',
    'gif': '图片',
    'bmp': '图片',
    'svg': '图片',
    'webp': '图片'
  }
  
  return typeMap[ext] || ext.toUpperCase() || '文件'
}

/**
 * 获取文件图标类名
 * @param {string} url 文件URL或文件名
 * @returns {string} Element UI图标类名
 */
export function getFileIcon(url) {
  if (!url) return 'el-icon-document'
  const fileName = getFileName(url)
  const ext = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase()
  
  // 视频文件
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v'].includes(ext)) {
    return 'el-icon-video-camera'
  }
  // 压缩包
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)) {
    return 'el-icon-folder-opened'
  }
  // 电子书
  if (['epub', 'pdf', 'mobi', 'azw'].includes(ext)) {
    return 'el-icon-reading'
  }
  // Office文档
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
    return 'el-icon-document'
  }
  // 图片
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
    return 'el-icon-picture'
  }
  // 文本
  if (['txt', 'md'].includes(ext)) {
    return 'el-icon-document'
  }
  // 默认
  return 'el-icon-document'
}

/**
 * 获取默认图片占位符
 * @returns {string} Base64编码的占位图片
 */
export function getDefaultImagePlaceholder() {
  return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMiAyMEMyOC42ODYzIDIwIDI2IDIyLjY4NjMgMjYgMjZDMjYgMjkuMzEzNyAyOC42ODYzIDMyIDMyIDMyQzM1LjMxMzcgMzIgMzggMjkuMzEzNyAzOCAyNkMzOCAyMi42ODYzIDM1LjMxMzcgMjAgMzIgMjBaIiBmaWxsPSIjQkFCQUJBIi8+CjxwYXRoIGQ9Ik0xNiA0OEwxOC4yIDEzLjJIMTQuNEwxMi4yIDQ4SDE2Wk0yMCA0OEgyOFYxMy4ySDIwVjQ4Wk0zNCA0OEg0MlYxMy4ySDM0VjQ4Wk00OCA0OEw0NS44IDEzLjJINDIuNEw0NC42IDQ4SDQ4WiIgZmlsbD0iI0JBQkFCQSIvPgo8L3N2Zz4K'
}

